import { useEffect, useState, useMemo } from "react";
import { Search, Filter, X, Sparkles } from "lucide-react";
import api from "../api";
import VacancyCard from "../components/VacancyCard";
import { EXPERTISE, PROVINCES } from "../lib/constants";
import { useAuth } from "../AuthContext";

export default function Vacatures() {
  const { user } = useAuth();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expertise, setExpertise] = useState("");
  const [province, setProvince] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (expertise) params.expertise = expertise;
    if (province) params.province = province;
    if (availableFrom) params.available_from = availableFrom;
    try {
      const { data } = await api.get("/vacancies", { params });
      setVacancies(data);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, expertise, province, availableFrom]);

  useEffect(() => {
    if (!user || user === false) { setUserProfile(null); return; }
    const url = user.role === "candidate" ? "/profile/candidate" : user.role === "client" ? "/profile/client" : null;
    if (!url) return;
    api.get(url).then(({ data }) => setUserProfile(data)).catch(() => setUserProfile(null));
  }, [user]);

  // Personalisation: split into matching + other
  const { matching, other, hasPersonalization } = useMemo(() => {
    if (!user || user === false || !userProfile) {
      return { matching: [], other: vacancies, hasPersonalization: false };
    }
    let userCats = [];
    if (user.role === "candidate") {
      userCats = (userProfile.expertise_categories && userProfile.expertise_categories.length > 0)
        ? userProfile.expertise_categories
        : (userProfile.expertise_category ? [userProfile.expertise_category] : []);
    } else if (user.role === "client") {
      userCats = userProfile.interest_areas || [];
    }
    if (userCats.length === 0) {
      return { matching: [], other: vacancies, hasPersonalization: false };
    }
    const m = vacancies.filter((v) => userCats.includes(v.expertise_category));
    const o = vacancies.filter((v) => !userCats.includes(v.expertise_category));
    return { matching: m, other: o, hasPersonalization: true };
  }, [vacancies, user, userProfile]);

  const clear = () => {
    setQ(""); setExpertise(""); setProvince(""); setAvailableFrom("");
  };

  const displayedOther = showAll ? other : [];

  return (
    <section className="ok-section">
      <div className="ok-container">
        <div className="ok-eyebrow mb-3">Vacatures</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          Open opdrachten in de publieke sector.
        </h1>
        <p className="text-[#4a5d5e] max-w-2xl">
          Filter op expertisegebied, provincie of beschikbaarheid. Inloggen is niet vereist om vacatures te bekijken.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start space-y-6">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Zoeken</label>
              <div className="relative mt-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7b8e8f]" />
                <input
                  data-testid="filter-search"
                  className="w-full pl-9 pr-3 py-2.5 border border-[#e2e8f0] text-sm bg-white"
                  placeholder="Functietitel of trefwoord"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Expertise</label>
              <select
                data-testid="filter-expertise"
                className="mt-2 w-full px-3 py-2.5 border border-[#e2e8f0] text-sm bg-white"
                value={expertise}
                onChange={(e) => setExpertise(e.target.value)}
              >
                <option value="">Alle categorieën</option>
                {EXPERTISE.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Provincie</label>
              <select
                data-testid="filter-province"
                className="mt-2 w-full px-3 py-2.5 border border-[#e2e8f0] text-sm bg-white"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
              >
                <option value="">Alle provincies</option>
                {PROVINCES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Beschikbaar vanaf</label>
              <input
                data-testid="filter-available-from"
                type="date"
                className="mt-2 w-full px-3 py-2.5 border border-[#e2e8f0] text-sm bg-white"
                value={availableFrom}
                onChange={(e) => setAvailableFrom(e.target.value)}
              />
            </div>
            <button onClick={clear} data-testid="filter-clear"
              className="inline-flex items-center gap-2 text-sm text-[#01696f] hover:underline">
              <X size={14} /> Filters wissen
            </button>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between text-sm text-[#4a5d5e] mb-5">
              <span data-testid="vacancy-count">
                {loading ? "Laden..." : hasPersonalization ? `${matching.length} matchende vacatures` : `${vacancies.length} vacatures gevonden`}
              </span>
              <span className="inline-flex items-center gap-1.5"><Filter size={14} /> Sorteren: meest recent</span>
            </div>

            {!loading && hasPersonalization && (
              <>
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#e2e8f0]">
                  <Sparkles size={16} className="text-[#01696f]" />
                  <span className="text-sm text-[#0a191a] font-medium">
                    {user.role === "candidate" ? "Op basis van jouw expertisegebieden" : "Op basis van uw interessegebieden"}
                  </span>
                </div>
                {matching.length === 0 ? (
                  <div className="border border-dashed border-[#e2e8f0] p-8 text-center mb-8">
                    <p className="text-sm text-[#7b8e8f]">Op dit moment geen matchende vacatures. Bekijk alle open opdrachten hieronder.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
                    {matching.map((v) => <VacancyCard key={v.id} v={v} />)}
                  </div>
                )}

                {other.length > 0 && (
                  <div className="border-t border-[#e2e8f0] pt-8">
                    {!showAll ? (
                      <button
                        data-testid="show-all-btn"
                        onClick={() => setShowAll(true)}
                        className="w-full border border-[#0a191a] py-3 rounded-sm hover:bg-[#0a191a] hover:text-white font-medium text-sm transition-colors"
                      >
                        Alles bekijken ({other.length} overige vacatures)
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-5">
                          <span className="text-sm text-[#4a5d5e]">Overige open vacatures</span>
                          <button
                            data-testid="hide-all-btn"
                            onClick={() => setShowAll(false)}
                            className="text-xs text-[#01696f] hover:underline"
                          >
                            Verbergen
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {displayedOther.map((v) => <VacancyCard key={v.id} v={v} />)}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {!loading && !hasPersonalization && (
              <>
                {vacancies.length === 0 && (
                  <p className="text-sm text-[#7b8e8f]">Geen vacatures gevonden. Pas je filters aan.</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {vacancies.map((v) => <VacancyCard key={v.id} v={v} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
